import {
  type JSX, useCallback, useEffect, useLayoutEffect, useRef, useState,
} from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import classNames from 'classnames';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';

import { TableComponent } from 'components/TableComponent';

import { ColumnDefinition, SortState } from './types';

import classes from './style.module.css';

// Above this many rows we window the body so we don't mount thousands of <tr>.
// Below it, the table renders exactly as before (no virtualization).
const VIRTUALIZE_ROW_THRESHOLD = 100;
// Initial guess for row height. The real height is measured from a rendered
// row after first paint (rows here are uniform height), so this only affects
// the very first frame before measurement.
const ESTIMATED_ROW_HEIGHT = 53;
const OVERSCAN = 8;

// The page content scrolls inside the layout's <main overflow-y:auto> element,
// not the window — so the virtualizer must observe that element's scroll.
// Prefer the layout <main>; fall back to the nearest ancestor that is actually
// scrolling (scrollHeight > clientHeight, not just an overflow-x:auto wrapper),
// then the document scroller.
const getScrollParent = (start: HTMLElement | null): HTMLElement => {
  const main = start?.closest('main');
  if (main) return main as HTMLElement;
  let node = start?.parentElement ?? null;
  while (node) {
    const { overflowY } = getComputedStyle(node);
    const scrollable = overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay';
    if (scrollable && node.scrollHeight > node.clientHeight) {
      return node;
    }
    node = node.parentElement;
  }
  return (document.scrollingElement as HTMLElement) ?? document.documentElement;
};

type SearchableTableContentProps<T = any> = {
  filteredData: T[];
  rowComponent: (item: T) => JSX.Element;
  columns: ColumnDefinition<T>[];
  sortState: SortState;
  onSort: (columnName: string) => void;
  virtualize?: 'auto' | boolean;
};

type VirtualizedTableBodyProps<T> = {
  filteredData: T[];
  rowComponent: (item: T) => JSX.Element;
  colSpan: number;
};

/**
 * Windowed <tbody> — only on-screen rows are mounted, so a 14k-row table no
 * longer blocks the main thread. Observes the page's real scroll container
 * (the layout's overflow:auto element), so scroll behavior is unchanged.
 * Uses spacer rows (rather than absolute positioning) so the table stays in
 * normal flow and the consumers' %/px column widths keep working.
 */
const VirtualizedTableBody = <T, >({
  filteredData, rowComponent, colSpan,
}: VirtualizedTableBodyProps<T>) => {
  const bodyRef = useRef<HTMLTableSectionElement>(null);
  // The actual scroll container (resolved from the DOM after mount).
  const [scrollEl, setScrollEl] = useState<HTMLElement | null>(null);
  // Distance from the top of the scroll container's content to the first row,
  // so the virtualizer knows where the list begins (below the panel + filters).
  const [scrollMargin, setScrollMargin] = useState(0);
  // Real per-row height, measured from a rendered row. Keeps the virtualizer's
  // total height (and the scrollbar) accurate so there's no gap at the bottom.
  const [rowHeight, setRowHeight] = useState(ESTIMATED_ROW_HEIGHT);

  // Resolve the scroll container once — it doesn't change for the table's
  // lifetime, so there's no need to re-walk the DOM on every render.
  useLayoutEffect(() => {
    if (bodyRef.current) setScrollEl(getScrollParent(bodyRef.current));
  }, []);

  const measure = useCallback(() => {
    const el = bodyRef.current;
    if (!el || !scrollEl) return;
    // Offset of the rows from the top of the scroll container's content,
    // independent of current scroll position. Read from the <tbody>'s own top,
    // which the virtualizer's spacer rows don't move — so this value never
    // depends on the virtualizer's output and can't feed back into it.
    const sm = el.getBoundingClientRect().top
      - scrollEl.getBoundingClientRect().top
      + scrollEl.scrollTop;
    setScrollMargin((prev) => (Math.abs(prev - sm) > 0.5 ? sm : prev));
    // First non-spacer row (spacers carry aria-hidden). Rows here are uniform
    // height, so one measurement gives the height for every row.
    const rowEl = el.querySelector<HTMLTableRowElement>('tr:not([aria-hidden="true"])');
    const h = rowEl?.getBoundingClientRect().height ?? 0;
    if (h > 0) setRowHeight((prev) => (Math.abs(prev - h) > 0.5 ? h : prev));
  }, [scrollEl]);

  // Re-measure only when an input that can actually change the offset or row
  // height changes: the resolved scroll container, the row count (filters/search
  // change the content above the list), or a viewport resize (below).
  //
  // It must NOT run on every commit. The virtualizer re-renders on every scroll
  // tick; measuring on each of those commits and writing scrollMargin back into
  // the virtualizer created a measure -> re-layout -> measure feedback loop that
  // never settled under continuous scrolling, hitting React's max update depth
  // (minified error #185 / "Unexpected Application Error" on scroll).
  useLayoutEffect(() => {
    measure();
  }, [measure, filteredData.length]);

  useEffect(() => {
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure]);

  const virtualizer = useVirtualizer({
    count: filteredData.length,
    getScrollElement: () => scrollEl,
    estimateSize: () => rowHeight,
    overscan: OVERSCAN,
    scrollMargin,
  });

  // estimateSize is only consulted while building the size cache, so once the
  // measured rowHeight lands we reset the cache to re-lay-out at the real size.
  useLayoutEffect(() => {
    virtualizer.measure();
  }, [virtualizer, rowHeight]);

  const virtualItems = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();

  const hasVirtualItems = virtualItems.length > 0;
  const paddingTop = hasVirtualItems
    ? virtualItems[0].start - scrollMargin
    : 0;
  const paddingBottom = hasVirtualItems
    ? totalSize - (virtualItems[virtualItems.length - 1].end - scrollMargin)
    : 0;

  return (
    <tbody ref={bodyRef} data-virtualized="true">
      {paddingTop > 0 && (
        <tr aria-hidden="true">
          <td colSpan={colSpan} style={{ height: paddingTop, padding: 0, border: 0 }} />
        </tr>
      )}
      {virtualItems.map((virtualRow) => rowComponent(filteredData[virtualRow.index]))}
      {paddingBottom > 0 && (
        <tr aria-hidden="true">
          <td colSpan={colSpan} style={{ height: paddingBottom, padding: 0, border: 0 }} />
        </tr>
      )}
    </tbody>
  );
};

// Table component used in SearchableTable
export const SearchableTableContent = <T, >({
  filteredData, rowComponent, columns, sortState, onSort, virtualize = 'auto',
}: SearchableTableContentProps<T>) => {
  const isVirtual = virtualize === true
    || (virtualize === 'auto' && filteredData.length > VIRTUALIZE_ROW_THRESHOLD);

  return (
    <TableComponent.TableContainer>
      <TableComponent.Table>
        <TableComponent.TableHeader>
          <TableComponent.TableRow>
            {columns.map((col) => {
              const isSortable = Boolean(col.sortFunction);
              const isActive = isSortable && sortState.columnName === col.name;
              return (
                <TableComponent.TableHeaderCell
                  key={col.name}
                  style={{ ...col.style, cursor: isSortable ? 'pointer' : 'default' }}
                  className={classNames(col.className, {
                    [classes.sortableHeader]: isSortable,
                    [classes.sortableHeaderActive]: isActive,
                  })}
                  onClick={() => isSortable && onSort(col.name)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span className={classNames({ [classes.sortableHeaderLabel]: isSortable })}>
                      {col.name}
                    </span>
                    {isSortable && (
                      <span style={{ display: 'flex', alignItems: 'center' }}>
                        {isActive
                          ? sortState.direction === 'asc'
                            ? <ArrowUp size={14} />
                            : <ArrowDown size={14} />
                          : <ArrowUpDown size={14} opacity={0.5} />
                        }
                      </span>
                    )}
                  </div>
                  {col.headerElement}
                </TableComponent.TableHeaderCell>
              );
            })}
          </TableComponent.TableRow>
        </TableComponent.TableHeader>
        {isVirtual ? (
          <VirtualizedTableBody
            filteredData={filteredData}
            rowComponent={rowComponent}
            colSpan={columns.length}
          />
        ) : (
          <TableComponent.TableBody>
            {filteredData.map((item) => rowComponent(item))}
          </TableComponent.TableBody>
        )}
      </TableComponent.Table>
      <TableComponent.TableFooterDiv />
    </TableComponent.TableContainer>
  );
};
